import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Building2, Calendar, Phone, Hash } from 'lucide-react';
import { format } from 'date-fns';
import PlaceAutocomplete from '../../components/common/PlaceAutocomplete';

const AccommodationModal = ({ 
  open, 
  onOpenChange, 
  details,
  onSave 
}) => {
  const { t } = useLanguage();
  const [localDetails, setLocalDetails] = useState(details || {});
  
  // Sync state when modal opens or details change
  useEffect(() => {
    if (open) {
      setLocalDetails(details || {});
    }
  }, [open, details]);

  const handleSave = () => {
    onSave(localDetails);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] !p-0">
        <DialogHeader className="p-4 sm:p-6 pb-2">
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Building2 className="w-5 h-5 text-orange-500" />
            {t('myTrip.accommodation.title')}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {t('myTrip.accommodation.description')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 px-4 sm:px-6 pb-4 overflow-y-auto">
          <div>
            <Label>{t('myTrip.accommodation.name')}</Label>
            <div className="relative mt-1">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={localDetails?.name || ''}
                onChange={(e) => setLocalDetails({ ...localDetails, name: e.target.value })}
                placeholder="Ex: Hotel Ibis, Airbnb Centro"
                className="pl-10"
              />
            </div>
          </div>

          <div>
            <Label>{t('myTrip.accommodation.address')}</Label>
            <div className="mt-1">
              <PlaceAutocomplete
                value={localDetails?.address_data || null}
                onChange={(place) => {
                  if (place) {
                    setLocalDetails({
                      ...localDetails,
                      address: place.formatted_address || place.name,
                      address_data: place
                    });
                  }
                }}
                placeholder={t('myTrip.accommodation.addressPlaceholder')}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>{t('myTrip.accommodation.checkIn')}</Label>
              <div className="relative mt-1">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="datetime-local"
                  value={localDetails?.check_in ? format(new Date(localDetails.check_in), "yyyy-MM-dd'T'HH:mm") : ''}
                  onChange={(e) => setLocalDetails({ 
                    ...localDetails, 
                    check_in: e.target.value ? new Date(e.target.value).toISOString() : null 
                  })}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label>{t('myTrip.accommodation.checkOut')}</Label>
              <div className="relative mt-1">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="datetime-local"
                  value={localDetails?.check_out ? format(new Date(localDetails.check_out), "yyyy-MM-dd'T'HH:mm") : ''}
                  onChange={(e) => setLocalDetails({ 
                    ...localDetails, 
                    check_out: e.target.value ? new Date(e.target.value).toISOString() : null 
                  })}
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>{t('myTrip.accommodation.phone')}</Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={localDetails?.contact_phone || ''}
                  onChange={(e) => setLocalDetails({ ...localDetails, contact_phone: e.target.value })}
                  placeholder="(11) 99999-9999"
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label>{t('myTrip.accommodation.bookingCode')}</Label>
              <div className="relative mt-1">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={localDetails?.booking_code || ''}
                  onChange={(e) => setLocalDetails({ ...localDetails, booking_code: e.target.value })}
                  placeholder="Código da reserva"
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          <div>
            <Label>{t('myTrip.accommodation.notes')}</Label>
            <Textarea
              value={localDetails?.notes || ''}
              onChange={(e) => setLocalDetails({ ...localDetails, notes: e.target.value })}
              placeholder={t('myTrip.accommodation.notesPlaceholder')}
              rows={3}
              className="mt-1"
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 p-4 sm:p-6 pt-2 border-t">
          <Button
            variant="outline"
            className="flex-1 rounded-full order-2 sm:order-1"
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            className="flex-1 rounded-full bg-orange-500 hover:bg-orange-600 order-1 sm:order-2"
            onClick={handleSave}
          >
            {t('common.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AccommodationModal;
